
// --- PURE GEOMETRY UTILITIES ---

// v2024.2: Centralized Geographic Coordinate Validation
// Replaces duplicated isValidGeoCoordinate functions across mappers
// Rejects 0,0 coordinates (Atlantic Ocean - indicates missing data)
export const isValidGeoCoordinate = (
    lat: number | undefined | null,
    lng: number | undefined | null
): boolean => {
    if (lat == null || lng == null) return false;
    if (typeof lat !== 'number' || typeof lng !== 'number') return false;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    
    // Valid geographic ranges: lat [-90, 90], lng [-180, 180]
    // Additionally reject 0,0 which indicates missing/unknown coordinates
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    
    // Reject null island (0, 0) - indicates missing geocoding data
    if (lat === 0 && lng === 0) return false;
    
    return true;
};

export const deg2rad = (deg: number) => deg * (Math.PI / 180);

// v55.1: Centralized Tortuosity Factor (Increased to 1.7 for Brazilian road network reality)
export const TORTUOSITY_FACTOR = 1.7;

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth Radius in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Calculates bearing (angle) between two points in degrees (0-360)
export const calculateBearing = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const dLon = deg2rad(lng2 - lng1);
  const y = Math.sin(dLon) * Math.cos(deg2rad(lat2));
  const x = Math.cos(deg2rad(lat1)) * Math.sin(deg2rad(lat2)) -
    Math.sin(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.cos(dLon);
  const brng = Math.atan2(y, x);
  return ((brng * 180 / Math.PI) + 360) % 360;
};
